import { Fragment } from 'react';
import {
	Box,
	Divider,
	Grid,
	IconButton,
	List,
	ListItem,
	ListItemText,
	TextField,
	Typography,
} from '@mui/material';
import { CopyIcon, LeftArrowIcon } from '../../assets/icons/Icons';
import CustomAccordion from '../../components/CustomAccordion';

const textInputBoarderStyle = {
	'& .MuiOutlinedInput-root': {
		'& fieldset': {
			border: 'none',
		},
		'&:hover fieldset': {
			border: '1px solid #e0dcdc',
		},
		'&.Mui-focused fieldset': {
			border: '1px solid #e0dcdc',
		},
		fontSize: '14px',
	},
};

function PlanTileTemplate({ planData, expanded, handleExpansion, onCopyPlan }) {
	function covertArrayToBulletString(array) {
		return array.map(item => `• ${item}`).join('\n');
	}
	return (
		<Grid
			container
			bgcolor={'#FFFFFF'}
			borderRadius={'5px'}
			padding={'10px'}>
			{planData ? (
				planData.map((value, index) => {
					return (
						<CustomAccordion
							key={index}
							id={index}
							expanded={expanded === index}
							handleExpansion={handleExpansion(index)}
							mainTitle={value.title}>
							<Grid container marginTop={'10px'}>
								{value.bodyContent.map((item, itemIndex) => {
									return (
										<Grid
											key={itemIndex}
											container
											paddingX={'15px'}
											flexDirection={'column'}>
											<Grid
												container
												justifyContent={'space-between'}
												alignItems={'center'}
												paddingX={'15px'}>
												<Typography
													fontSize={'14px'}
													color={'#28696D'}
													fontWeight={'600'}>
													{item.label}
												</Typography>
												<IconButton
													disableFocusRipple
													onClick={() =>
														onCopyPlan(item.content)
													}
													style={{
														padding: '0px',
													}}>
													<CopyIcon />
												</IconButton>
											</Grid>
											<Box padding={'15px'}>
												<TextField
													multiline
													fullWidth
													value={covertArrayToBulletString(
														item.content,
													)}
													InputProps={{
														readOnly: true,
													}}
													variant="outlined"
													placeholder=""
													sx={textInputBoarderStyle}
												/>
											</Box>
										</Grid>
									);
								})}
							</Grid>
						</CustomAccordion>
					);
				})
			) : (
				<Grid container sx={{ justifyContent: 'center' }}>
					<Typography variant="tableBody">
						No data available
					</Typography>
				</Grid>
			)}
		</Grid>
	);
}

function PlanNormalTemplate({ planData }) {
	const renderValue = value => {
		if (Array.isArray(value)) {
			return (
				<List dense sx={{ py: 0 }}>
					{value.map((item, idx) => (
						<Fragment key={idx}>
							{idx > 0 && <Divider variant="fullWidth" />}
							<ListItem sx={{ m: 0, pt: 0 }}>
								<List dense sx={{ py: 0 }}>
									{Object.entries(item)
										.filter(
											([_, v]) => v != null && v !== '',
										)
										.map(([k, v]) => (
											<ListItem
												key={k}
												sx={{ m: 0, pt: 0 }}>
												<ListItemText
													primary={k}
													secondary={v}
													primaryTypographyProps={{
														sx: {
															fontWeight: '600',
															color: 'text.primary',
														},
													}}
													secondaryTypographyProps={{
														sx: {
															color: 'text.secondary',
														},
													}}
												/>
											</ListItem>
										))}
								</List>
							</ListItem>
						</Fragment>
					))}
				</List>
			);
		} else if (typeof value === 'object' && value !== null) {
			return (
				<List dense sx={{ py: 0 }}>
					{Object.entries(value)
						.filter(([_, v]) => v != null && v !== '')
						.map(([k, v]) => (
							<ListItem key={k} sx={{ m: 0, pt: 0 }}>
								<ListItemText
									primary={k}
									secondary={v}
									primaryTypographyProps={{
										sx: {
											fontWeight: '600',
											color: 'text.primary',
										},
									}}
									secondaryTypographyProps={{
										sx: { color: 'text.secondary' },
									}}
								/>
							</ListItem>
						))}
				</List>
			);
		} else {
			return value;
		}
	};

	return (
		<Grid
			width={'100%'}
			bgcolor={'#FFFFFF'}
			borderRadius={'5px'}
			padding={'10px 10px 10px 15px'}>
			<List dense sx={{ py: 0 }}>
				{Object.entries(planData)
					.filter(([_, v]) => v != null && v !== '')
					.map(([key, value]) => (
						<ListItem key={key} sx={{ py: 0.5 }}>
							<ListItemText
								primary={key}
								secondary={renderValue(value)}
								primaryTypographyProps={{
									sx: {
										fontWeight: '600',
										color: 'text.primary',
									},
								}}
								secondaryTypographyProps={{
									sx: { color: 'text.secondary' },
								}}
							/>
						</ListItem>
					))}
			</List>
		</Grid>
	);
}

function PlanOverview({
	planData,
	planType,
	goBack,
	expanded,
	handleExpansion,
	selectedElder,
	planTemplate = 'Tile',
}) {
	function onCopyPlan(data) {
		const value = data.map(item => `• ${item}`).join('\n');

		if (navigator.clipboard) {
			navigator.clipboard.writeText(value);
		} else {
			const tempTextArea = document.createElement('textarea');
			tempTextArea.value = value;
			document.body.appendChild(tempTextArea);
			tempTextArea.select();
			document.execCommand('copy');
			document.body.removeChild(tempTextArea);
		}
	}

	function onRenderPlanTemplate() {
		if (planTemplate && planTemplate === 'Tile') {
			return (
				<PlanTileTemplate
					planData={planData}
					expanded={expanded}
					handleExpansion={handleExpansion}
					onCopyPlan={onCopyPlan}
				/>
			);
		} else {
			return <PlanNormalTemplate planData={planData} />;
		}
	}

	return (
		<Grid
			container
			sx={{
				marginLeft: '30px',
				marginTop: '16px',
				padding: '16px',
				backgroundColor: '#FFFFFF',
			}}>
			<Grid
				container
				alignItems={'center'}
				justifyContent={'space-between'}
				flexDirection={'row'}>
				<Grid
					display={'flex'}
					alignItems={'center'}
					flexDirection={'row'}>
					<IconButton disableRipple onClick={goBack}>
						<LeftArrowIcon />
					</IconButton>
					<Typography
						color={'#191919'}
						fontSize={'18px'}
						marginLeft={'5px'}
						fontWeight={'600'}>
						{planType}
					</Typography>
				</Grid>
				<Grid
					container
					bgcolor={'#FFFFFF'}
					borderRadius={'5px'}
					marginTop={'10px'}
					alignItems={'center'}
					justifyContent={'space-between'}
					flexDirection={'row'}
					padding={'10px'}>
					<Typography
						fontSize={'16px'}
						color={
							'#28696D'
						}>{`Elder ID: ${selectedElder}`}</Typography>
				</Grid>
				{onRenderPlanTemplate()}
			</Grid>
		</Grid>
	);
}

export default PlanOverview;
